import {
  Box,
  Button,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  Image,
  Text,
  useDisclosure,
} from "@chakra-ui/react";

import { BottomNavProps, NavLinkItem } from "../../@types/Navbar";

const DEFAULT_MORE_ICON =
  "<svg viewBox='0 0 24 24' xmlns='http://www.w3.org/2000/svg'><circle cx='5' cy='12' r='2'/><circle cx='12' cy='12' r='2'/><circle cx='19' cy='12' r='2'/></svg>";

function NavItemIcon({
  icon,
  label,
  iconProps,
  tint = false,
}: Pick<NavLinkItem, "icon" | "label"> & {
  iconProps?: BottomNavProps["iconProps"];
  /** Tint image icons with currentColor via CSS mask. */
  tint?: boolean;
}) {
  if (!icon) return null;

  if (typeof icon === "string") {
    if (icon.trim().startsWith("<svg")) {
      return (
        <Box
          dangerouslySetInnerHTML={{ __html: icon }}
          sx={{
            "& svg": {
              width: "22px",
              height: "22px",
              stroke: "currentColor",
              fill: "currentColor",
            },
          }}
        />
      );
    }
    // Image icon: optionally tint it with the current text color so it
    // recolors with the active/inactive nav state, just like an SVG.
    if (tint) {
      return (
        <Box
          role="img"
          aria-label={label}
          boxSize="22px"
          bg="currentColor"
          sx={{
            maskImage: `url(${icon})`,
            WebkitMaskImage: `url(${icon})`,
            maskRepeat: "no-repeat",
            WebkitMaskRepeat: "no-repeat",
            maskPosition: "center",
            WebkitMaskPosition: "center",
            maskSize: "contain",
            WebkitMaskSize: "contain",
          }}
          {...iconProps}
        />
      );
    }
    return <Image src={icon} alt={label} boxSize="22px" {...iconProps} />;
  }
  if (typeof icon === "function") {
    return <Box as={icon} />;
  }
  return <>{icon}</>;
}

/**
 * Fixed bottom navigation bar for the mobile / native-app experience.
 * Hidden on desktop (>= xl), where the horizontal navbar takes over.
 * Items reuse the existing NavLinkItem shape (label/href/icon/authRequired).
 * An optional trailing "More" tab opens a bottom sheet that hosts the
 * footer links and account actions (footer is hidden on mobile).
 */
function BottomNav({
  navLinkItems = [],
  activeHref,
  routeChangeFn,
  isLoggedIn = false,
  tintImageIcons = false,
  showLabels = true,
  showMoreTab = true,
  moreLabel = "More",
  moreItems = [],
  showRegister = true,
  onLogin,
  onRegister,
  onLogout,
  moreSheetTitle,
  bottomNavContainerProps,
  bottomNavItemProps,
  bottomNavActiveItemProps,
  bottomNavIconWrapperProps,
  bottomNavLabelProps,
  bottomNavActiveLabelProps,
  iconProps,
  bottomNavMoreOverlayProps,
  bottomNavMoreContentContainerProps,
  bottomNavMoreHandleProps,
  bottomNavMoreHeaderProps,
  bottomNavMoreCloseButtonProps,
  bottomNavMoreBodyProps,
  bottomNavMoreListProps,
  bottomNavMoreItemProps,
  bottomNavMoreActiveItemProps,
  bottomNavMoreIconWrapperProps,
  bottomNavMoreActionsWrapperProps,
  bottomNavMoreLoginButtonProps,
  bottomNavMoreRegisterButtonProps,
  bottomNavMoreLogoutButtonProps,
}: BottomNavProps) {
  const { isOpen, onOpen, onClose } = useDisclosure();

  const items = navLinkItems.filter(
    (item) => item.href && !(item.authRequired && !isLoggedIn),
  );

  const sheetLinks = moreItems.filter(
    (item) => item.href && !(item.authRequired && !isLoggedIn),
  );
  const hasMoreTab =
    showMoreTab && (sheetLinks.length > 0 || !!onLogin || isLoggedIn);

  if (items.length === 0 && !hasMoreTab) return null;

  const tabBaseProps = {
    variant: "ghost" as const,
    flex: 1,
    h: "auto",
    minW: 0,
    py: 1.5,
    px: 1,
    display: "flex",
    flexDirection: "column" as const,
    alignItems: "center",
    justifyContent: "center",
    gap: 0.5,
    borderRadius: "none" as const,
    colorScheme: "transparent",
    _hover: { color: "primary.500", bg: "transparent" },
    _active: { bg: "transparent" },
  };

  const renderLabel = (label: string, isActive: boolean) =>
    showLabels && (
      <Text
        fontSize="0.65rem"
        fontWeight={isActive ? 700 : 500}
        noOfLines={1}
        {...bottomNavLabelProps}
        {...(isActive && bottomNavActiveLabelProps)}
      >
        {label}
      </Text>
    );

  return (
    <>
      <Flex
        as="nav"
        aria-label="bottom navigation"
        className="bottom-nav"
        display={{ base: "flex", xl: "none" }}
        position="fixed"
        bottom={0}
        left={0}
        right={0}
        zIndex="sticky"
        bg="background.nav"
        borderTop="1px solid"
        borderColor="gray.200"
        justifyContent="space-around"
        alignItems="stretch"
        pt={1}
        pb="env(safe-area-inset-bottom)"
        boxShadow="0 -2px 8px rgba(0,0,0,0.06)"
        {...bottomNavContainerProps}
      >
        {items.map((item, index) => {
          const isActive = item.href === activeHref;
          return (
            <Button
              key={`bottom_nav__${index}`}
              {...tabBaseProps}
              color={isActive ? "primary.500" : "gray.600"}
              onClick={() => item.href && routeChangeFn(item.href)}
              {...bottomNavItemProps}
              {...(isActive && bottomNavActiveItemProps)}
            >
              <Box
                as="span"
                fontSize="xl"
                lineHeight={1}
                {...bottomNavIconWrapperProps}
              >
                <NavItemIcon
                  icon={item.icon}
                  label={item.label}
                  iconProps={iconProps}
                  tint={tintImageIcons}
                />
              </Box>
              {renderLabel(item.label, isActive)}
            </Button>
          );
        })}

        {hasMoreTab && (
          <Button
            key="bottom_nav__more"
            aria-label={moreLabel}
            {...tabBaseProps}
            color={isOpen ? "primary.500" : "gray.600"}
            onClick={onOpen}
            {...bottomNavItemProps}
            {...(isOpen && bottomNavActiveItemProps)}
          >
            <Box
              as="span"
              fontSize="xl"
              lineHeight={1}
              {...bottomNavIconWrapperProps}
            >
              <NavItemIcon icon={DEFAULT_MORE_ICON} label={moreLabel} />
            </Box>
            {renderLabel(moreLabel, isOpen)}
          </Button>
        )}
      </Flex>

      {hasMoreTab && (
        <Drawer
          isOpen={isOpen}
          onClose={onClose}
          placement="bottom"
          autoFocus={false}
        >
          <DrawerOverlay {...bottomNavMoreOverlayProps} />
          <DrawerContent
            borderTopRadius="1rem"
            maxH="80vh"
            pb="env(safe-area-inset-bottom)"
            {...bottomNavMoreContentContainerProps}
          >
            {/* grab handle */}
            <Box
              w="2.5rem"
              h="0.3rem"
              bg="gray.300"
              borderRadius="full"
              mx="auto"
              mt={3}
              {...bottomNavMoreHandleProps}
            />
            {moreSheetTitle && (
              <DrawerHeader pb={2} {...bottomNavMoreHeaderProps}>
                {moreSheetTitle}
              </DrawerHeader>
            )}
            <DrawerCloseButton {...bottomNavMoreCloseButtonProps} />
            <DrawerBody
              pt={moreSheetTitle ? 0 : 4}
              px={4}
              pb={6}
              {...bottomNavMoreBodyProps}
            >
              <Flex direction="column" gap={1} {...bottomNavMoreListProps}>
                {sheetLinks.map((item, index) => {
                  const isActive = item.href === activeHref;
                  return (
                    <Button
                      key={`more_link__${index}`}
                      variant="ghost"
                      justifyContent="flex-start"
                      gap={3}
                      w="full"
                      borderRadius="md"
                      colorScheme={isActive ? "primary" : "gray"}
                      color={isActive ? "primary.500" : "gray.700"}
                      onClick={() => {
                        if (item.href) routeChangeFn(item.href);
                        onClose();
                      }}
                      {...bottomNavMoreItemProps}
                      {...(isActive && bottomNavMoreActiveItemProps)}
                    >
                      {item.icon && (
                        <Box
                          as="span"
                          fontSize="lg"
                          lineHeight={1}
                          {...bottomNavMoreIconWrapperProps}
                        >
                          <NavItemIcon
                            icon={item.icon}
                            label={item.label}
                            iconProps={iconProps}
                            tint={tintImageIcons}
                          />
                        </Box>
                      )}
                      {item.label}
                    </Button>
                  );
                })}
              </Flex>

              {/* Account actions */}
              <Box
                mt={sheetLinks.length ? 4 : 0}
                pt={sheetLinks.length ? 4 : 0}
                borderTop={sheetLinks.length ? "1px solid" : undefined}
                borderColor="gray.200"
                {...bottomNavMoreActionsWrapperProps}
              >
                {!isLoggedIn ? (
                  <Flex gap={3}>
                    <Button
                      flex={1}
                      colorScheme="primary"
                      onClick={() => {
                        onLogin?.();
                        onClose();
                      }}
                      {...bottomNavMoreLoginButtonProps}
                    >
                      Login
                    </Button>
                    {showRegister && (
                      <Button
                        flex={1}
                        variant="outline"
                        colorScheme="primary"
                        onClick={() => {
                          onRegister?.();
                          onClose();
                        }}
                        {...bottomNavMoreRegisterButtonProps}
                      >
                        Register
                      </Button>
                    )}
                  </Flex>
                ) : (
                  <Button
                    w="full"
                    variant="outline"
                    colorScheme="red"
                    onClick={() => {
                      onLogout?.();
                      onClose();
                    }}
                    {...bottomNavMoreLogoutButtonProps}
                  >
                    Logout
                  </Button>
                )}
              </Box>
            </DrawerBody>
          </DrawerContent>
        </Drawer>
      )}
    </>
  );
}

export default BottomNav;
